import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useMenuViewModel } from "../../viewmodels/menu/useMenuViewModel";

interface Props {
  sidebarOpen: boolean;
  setSidebarOpen: (value: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: Props) => {
  const location = useLocation();
  const [openSection, setOpenSection] = useState<string | null>(null);

  const { menus } = useMenuViewModel();

  const toggleSection = (id: string) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  const baseClass =
    "flex items-center gap-2 px-4 py-2 text-sm rounded transition-colors";

  const activeClass = "bg-primary text-white";
  const inactiveClass = "hover:bg-slate-100";

  // Auto-open dropdown based on current route
  useEffect(() => {
    menus.forEach((menu: any) => {
      if (menu.children?.length) {
        const isMatch = menu.children.some((child: any) =>
          location.pathname.startsWith(child.route || "")
        );

        if (isMatch) {
          setOpenSection(menu.id);
        }
      }
    });
  }, [location.pathname, menus]);

  return (
    <>
      {/* MOBILE OVERLAY */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed lg:static top-0 left-0 h-full w-64
          bg-white border-r border-slate-200
          transform ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }
          lg:translate-x-0 transition-transform duration-300
          z-30 flex flex-col overflow-y-auto`}
      >
        <div className="p-4 space-y-2">
          {/* TITLE */}
          <h2 className="text-xl font-bold mb-4">ERP</h2>

          {/* DYNAMIC MENUS */}
          {menus.map((menu: any) => {
            const hasChildren =
              menu.children && menu.children.length > 0;

            // Simple menu
            if (!hasChildren) {
              return (
                <NavLink
                  key={menu.id}
                  to={menu.route || "#"}
                  className={({ isActive }) =>
                    `${baseClass} ${
                      isActive ? activeClass : inactiveClass
                    }`
                  }
                >
                  {menu.name}
                </NavLink>
              );
            }

            // Dropdown menu
            return (
              <div key={menu.id}>
                <button
                  onClick={() => toggleSection(menu.id)}
                  className={`${baseClass} w-full justify-between`}
                >
                  <span>{menu.name}</span>
                  <span>{openSection === menu.id ? "▾" : "▸"}</span>
                </button>

                {openSection === menu.id && (
                  <div className="ml-4 mt-1 space-y-1">
                    {menu.children.map((child: any) => (
                      <NavLink
                        key={child.id}
                        to={child.route}
                        className={({ isActive }) =>
                          `${baseClass} ${
                            isActive ? activeClass : inactiveClass
                          }`
                        }
                      >
                        {child.name}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;